import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MapPin, List, Map as MapIcon, Navigation } from "lucide-react";
import * as Icons from "lucide-react";
import api from "@/lib/api";
import LocalCard from "@/components/LocalCard";

const RADII = [1, 5, 10, 25, 50];

export default function Local() {
  const [params, setParams] = useSearchParams();
  const [offers, setOffers] = useState([]);
  const [cats, setCats] = useState([]);
  const [view, setView] = useState("list");
  const [location] = useState("Sarasota, FL");

  const category = params.get("category") || "";
  const radius = params.get("radius") || "";
  const sort = params.get("sort") || "distance";
  const setParam = (k, v) => { const p = new URLSearchParams(params); v ? p.set(k, v) : p.delete(k); setParams(p); };

  useEffect(() => {
    const qs = new URLSearchParams();
    if (category) qs.set("category", category);
    if (radius) qs.set("radius", radius);
    if (sort) qs.set("sort", sort);
    api.get(`/local/offers?${qs.toString()}`).then((r) => setOffers(r.data));
  }, [category, radius, sort]);
  useEffect(() => { api.get("/local-categories").then((r) => setCats(r.data)); }, []);

  return (
    <div>
      <section className="relative overflow-hidden grain" style={{ background: "linear-gradient(160deg,#E4FBF1 0%,#FAFAF9 60%,#E6F6FF 100%)" }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white friib-shadow text-xs font-bold text-stone-600 mb-4"><MapPin size={14} style={{ color: "var(--mint)" }} /> Your city has Friibs waiting</span>
          <h1 className="font-display text-4xl lg:text-5xl font-extrabold text-stone-900 leading-tight">Discover Friibs near you.</h1>
          <p className="mt-3 text-lg text-stone-600 max-w-xl">Local businesses are giving away food, drinks, experiences and more to introduce you to what they do.</p>
          <div className="mt-5 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white friib-shadow font-bold text-stone-800" data-testid="location-badge">
            <Navigation size={16} style={{ color: "var(--mint)" }} /> Friibs near {location}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Category pills */}
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-3 mb-4">
          <button onClick={() => setParam("category", "")} className={`shrink-0 px-4 py-2 rounded-full text-sm font-bold ${!category ? "text-white" : "bg-stone-100 text-stone-600"}`} style={{ background: !category ? "var(--mint)" : undefined }}>All</button>
          {cats.map((c) => {
            const Icon = Icons[c.icon] || Icons.Tag;
            return <button key={c.slug} data-testid={`local-cat-${c.slug}`} onClick={() => setParam("category", c.slug)} className={`shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold ${category === c.slug ? "text-white" : "bg-stone-100 text-stone-600"}`} style={{ background: category === c.slug ? "var(--mint)" : undefined }}><Icon size={15} /> {c.name}</button>;
          })}
        </div>

        <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <select value={radius} onChange={(e) => setParam("radius", e.target.value)} data-testid="radius-select" className="px-4 py-2 rounded-full border border-stone-200 bg-white text-sm font-semibold">
              <option value="">Any distance</option>{RADII.map((r) => <option key={r} value={r}>Within {r} mi</option>)}
            </select>
            <select value={sort} onChange={(e) => setParam("sort", e.target.value)} data-testid="local-sort" className="px-4 py-2 rounded-full border border-stone-200 bg-white text-sm font-semibold">
              <option value="distance">Closest</option><option value="popular">Most Popular</option><option value="value">Highest Value</option><option value="ending">Almost Gone</option>
            </select>
          </div>
          <div className="flex rounded-full bg-stone-100 p-1">
            <button onClick={() => setView("list")} className={`px-3 py-1.5 rounded-full text-sm font-bold flex items-center gap-1.5 ${view === "list" ? "bg-white friib-shadow" : "text-stone-500"}`} data-testid="view-list"><List size={15} /> List</button>
            <button onClick={() => setView("map")} className={`px-3 py-1.5 rounded-full text-sm font-bold flex items-center gap-1.5 ${view === "map" ? "bg-white friib-shadow" : "text-stone-500"}`} data-testid="view-map"><MapIcon size={15} /> Map</button>
          </div>
        </div>

        {view === "list" ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {offers.map((o, i) => <LocalCard key={o.slug} o={o} index={i} />)}
          </div>
        ) : (
          <div className="rounded-3xl overflow-hidden border border-stone-200 friib-shadow relative" data-testid="local-map">
            <div className="h-[480px] bg-[#e8eef0] relative" style={{ backgroundImage: "linear-gradient(rgba(0,184,132,.06) 1px,transparent 1px),linear-gradient(90deg,rgba(0,184,132,.06) 1px,transparent 1px)", backgroundSize: "40px 40px" }}>
              {offers.map((o, i) => (
                <div key={o.slug} className="absolute" style={{ top: `${20 + (i * 63) % 380}px`, left: `${12 + (i * 137) % 80}%` }}>
                  <a href={`/local/${o.slug}`} className="group flex flex-col items-center">
                    <span className="px-3 py-1.5 rounded-full bg-white friib-shadow text-xs font-bold text-stone-800 mb-1 whitespace-nowrap group-hover:scale-110 transition-transform">{o.title} · {o.distance}mi</span>
                    <MapPin size={30} className="fill-white" style={{ color: "var(--mint)" }} />
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
